/**
 * EP-157 T157-005 — the Settings → "แอปมือถือ" tab (spec §7 S1-S3). Owner/
 * admin only, and only on an account with settings.app_integration_enabled,
 * switched on from admin-cli (owner, 2026-10-08, replacing decision 5's
 * empty state); the caller gates both and the API refuses otherwise. The form
 * shows straight away.
 *
 * Holds one account's app_integrations row: name, iOS bundle id, Android
 * package name, Firebase project id, and the Firebase service account key —
 * a .json file read client-side via FileReader and sent as text, never
 * displayed after save (only "ตั้งค่าแล้ว" + the client email the key
 * belongs to; uploading a new file replaces it). "Test connection" proves
 * the saved key against Firebase Cloud Messaging without ever sending a real
 * push, and renders the two checks plus the last-verified time. The steps
 * done in Firebase and Apple are listed with a how-to link each
 * (ManualSetupSteps).
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, X } from 'lucide-react'
import { Card, Button, Input, Alert, FileInput, ConfirmDialog, Skeleton } from '@/components/ui'
import { ManualSetupSteps } from './ManualSetupSteps'
import {
  useAppIntegration,
  useUpdateAppIntegration,
  useDeleteAppIntegration,
  useTestAppIntegrationConnection,
} from '@/hooks/useAppIntegration'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useDateTimeFormatter } from '@/hooks/useDateFormatter'
import { useNotificationStore } from '@/stores/useNotificationStore'

type CheckState = true | false | null // null = not reached by the last attempt

function checksFromErrorCode(code: string | undefined): {
  keyValid: CheckState
  fcmReachable: CheckState
} {
  switch (code) {
    case 'app_integration_key_invalid':
      return { keyValid: false, fcmReachable: null }
    case 'app_integration_fcm_unreachable':
      return { keyValid: true, fcmReachable: false }
    default:
      return { keyValid: null, fcmReachable: null }
  }
}

function CheckRow({ state, label }: { state: CheckState; label: string }) {
  return (
    <div className="flex items-center gap-1.5 text-sm">
      {state === true && <Check className="w-4 h-4 text-success shrink-0" aria-hidden="true" />}
      {state === false && <X className="w-4 h-4 text-error shrink-0" aria-hidden="true" />}
      {state === null && <span className="w-4 h-4 shrink-0" aria-hidden="true" />}
      <span className={state === false ? 'text-error' : ''}>{label}</span>
    </div>
  )
}

interface AppIntegrationSettingsCardProps {
  canEdit: boolean
}

export function AppIntegrationSettingsCard({ canEdit }: AppIntegrationSettingsCardProps) {
  const { t } = useTranslation('settings')
  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('settings')
  const formatDateTime = useDateTimeFormatter()

  const { data: appIntegration, isLoading } = useAppIntegration()
  const updateAppIntegration = useUpdateAppIntegration()
  const deleteAppIntegration = useDeleteAppIntegration()
  const testConnection = useTestAppIntegrationConnection()

  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false)

  const [name, setName] = useState<string | null>(null)
  const [iosBundleId, setIosBundleId] = useState<string | null>(null)
  const [androidPackageName, setAndroidPackageName] = useState<string | null>(null)
  const [firebaseProjectId, setFirebaseProjectId] = useState<string | null>(null)
  // The new key's content, read client-side, never persisted to this
  // component's own state beyond the single Save that sends it — cleared
  // right after a successful save so it can never be re-sent or displayed.
  const [newServiceAccountJson, setNewServiceAccountJson] = useState('')
  const [newFileName, setNewFileName] = useState('')

  const resolvedName = name ?? appIntegration?.name ?? ''
  const resolvedIosBundleId = iosBundleId ?? appIntegration?.ios_bundle_id ?? ''
  const resolvedAndroidPackageName = androidPackageName ?? appIntegration?.android_package_name ?? ''
  const resolvedFirebaseProjectId = firebaseProjectId ?? appIntegration?.firebase_project_id ?? ''

  const [testErrorCode, setTestErrorCode] = useState<string | null>(null)
  const [testErrorMessage, setTestErrorMessage] = useState<string | null>(null)
  const [testSucceeded, setTestSucceeded] = useState(false)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      setNewServiceAccountJson(typeof reader.result === 'string' ? reader.result : '')
      setNewFileName(file.name)
    }
    reader.readAsText(file)
  }

  const handleSave = async () => {
    try {
      const saved = await updateAppIntegration.mutateAsync({
        name: resolvedName,
        ios_bundle_id: resolvedIosBundleId || undefined,
        android_package_name: resolvedAndroidPackageName || undefined,
        firebase_project_id: resolvedFirebaseProjectId,
        ...(newServiceAccountJson ? { firebase_service_account_json: newServiceAccountJson } : {}),
      })
      setNewServiceAccountJson('')
      setNewFileName('')
      setName(null)
      setIosBundleId(null)
      setAndroidPackageName(null)
      setFirebaseProjectId(null)
      setTestErrorCode(null)
      setTestErrorMessage(null)
      setTestSucceeded(false)
      addToast({ type: 'success', message: t('app_integration.save_success') })
      return saved
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
      return undefined
    }
  }

  const handleRemove = async () => {
    try {
      await deleteAppIntegration.mutateAsync()
      setRemoveConfirmOpen(false)
      setTestErrorCode(null)
      setTestErrorMessage(null)
      setTestSucceeded(false)
      addToast({ type: 'success', message: t('app_integration.remove_success') })
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  const handleTestConnection = () => {
    testConnection.mutate(undefined, {
      onSuccess: () => {
        setTestSucceeded(true)
        setTestErrorCode(null)
        setTestErrorMessage(null)
      },
      onError: (err) => {
        setTestSucceeded(false)
        const code = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code
        setTestErrorCode(code ?? null)
        const message = translateError(err)
        setTestErrorMessage(message)
        addToast({ type: 'error', message })
      },
    })
  }

  const checks = testSucceeded
    ? { keyValid: true as CheckState, fcmReachable: true as CheckState }
    : testErrorCode !== null
      ? checksFromErrorCode(testErrorCode)
      : null

  if (isLoading) {
    return <Card><Skeleton className="h-32" /></Card>
  }

  return (
    <Card>
      <div className="flex items-start justify-between gap-3 mb-1">
        <h3 className="font-semibold">{t('app_integration.title')}</h3>
      </div>

      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <p className="text-sm">
          {appIntegration?.verified_at ? (
            <>
              <span className="text-success">●</span>{' '}
              {t('app_integration.status_connected', { date: formatDateTime(appIntegration.verified_at) })}
            </>
          ) : (
            <>
              <span className="text-base-content/40">●</span> {t('app_integration.status_not_connected')}
            </>
          )}
        </p>
        {canEdit && appIntegration && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            loading={testConnection.isPending}
            onClick={handleTestConnection}
            data-testid="app-integration-test-connection"
          >
            {t('app_integration.test_connection')}
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="min-w-0">
          <Input
            label={t('app_integration.name_label')}
            name="app_integration_name"
            value={resolvedName}
            onChange={(e) => setName(e.target.value)}
            disabled={!canEdit}
          />
        </div>
        <div className="min-w-0">
          <Input
            label={t('app_integration.firebase_project_id_label')}
            name="app_integration_firebase_project_id"
            value={resolvedFirebaseProjectId}
            onChange={(e) => setFirebaseProjectId(e.target.value)}
            disabled={!canEdit}
          />
        </div>
        <div className="min-w-0">
          <Input
            label={t('app_integration.ios_bundle_id_label')}
            name="app_integration_ios_bundle_id"
            value={resolvedIosBundleId}
            onChange={(e) => setIosBundleId(e.target.value)}
            disabled={!canEdit}
            placeholder="com.example.app"
          />
        </div>
        <div className="min-w-0">
          <Input
            label={t('app_integration.android_package_name_label')}
            name="app_integration_android_package_name"
            value={resolvedAndroidPackageName}
            onChange={(e) => setAndroidPackageName(e.target.value)}
            disabled={!canEdit}
            placeholder="com.example.app"
          />
        </div>
      </div>

      <div className="mt-4">
        <p className="text-sm font-medium mb-1">{t('app_integration.service_account_label')}</p>
        {appIntegration?.service_account_present && !newFileName && (
          <p className="text-sm text-base-content/70 mb-2">
            {t('app_integration.service_account_configured', { email: appIntegration.firebase_client_email })}
          </p>
        )}
        {newFileName && (
          <p className="text-sm text-base-content/70 mb-2">
            {t('app_integration.service_account_new_file_selected', { filename: newFileName })}
          </p>
        )}
        {canEdit && (
          <FileInput
            size="sm"
            accept=".json,application/json"
            onChange={handleFileChange}
            label={
              appIntegration?.service_account_present
                ? t('app_integration.service_account_replace_label')
                : t('app_integration.service_account_upload_label')
            }
            data-testid="app-integration-service-account-file"
          />
        )}
      </div>

      {checks && (
        <div className="mt-4">
          <p className="text-xs font-medium text-base-content/60 mb-2">{t('app_integration.auto_checks_heading')}</p>
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            <CheckRow state={checks.keyValid} label={t('app_integration.check_key_valid')} />
            <CheckRow state={checks.fcmReachable} label={t('app_integration.check_fcm_reachable')} />
          </div>
          {testErrorMessage && (
            <Alert variant="error" className="mt-2">
              {testErrorMessage}
            </Alert>
          )}
        </div>
      )}

      <ManualSetupSteps
        heading={t('app_integration.manual_checks_heading')}
        linkLabel={t('app_integration.how_to')}
        testId="app-integration-manual-steps"
        steps={[
          { text: t('app_integration.manual_step_apns_key'), href: 'https://developer.apple.com/help/account/keys/create-a-private-key/' },
          { text: t('app_integration.manual_step_apns_upload'), href: 'https://firebase.google.com/docs/cloud-messaging/ios/certs' },
          { text: t('app_integration.manual_step_service_account'), href: 'https://firebase.google.com/docs/admin/setup' },
          { text: t('app_integration.manual_step_push_app_id'), href: 'https://developer.apple.com/help/account/identifiers/enable-app-capabilities/' },
        ]}
      />

      {canEdit && (
        <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
          {appIntegration && (
            <Button
              type="button"
              variant="ghost"
              className="text-error"
              onClick={() => setRemoveConfirmOpen(true)}
              data-testid="app-integration-remove"
            >
              {t('app_integration.remove')}
            </Button>
          )}
          <Button
            type="button"
            loading={updateAppIntegration.isPending}
            onClick={handleSave}
            data-testid="app-integration-save"
            className="ml-auto"
          >
            {t('app_integration.save')}
          </Button>
        </div>
      )}

      <ConfirmDialog
        isOpen={removeConfirmOpen}
        onClose={() => setRemoveConfirmOpen(false)}
        onConfirm={handleRemove}
        title={t('app_integration.remove_confirm_title')}
        message={t('app_integration.remove_confirm_message')}
        confirmLabel={t('app_integration.remove_confirm_button')}
        variant="warning"
        loading={deleteAppIntegration.isPending}
      />
    </Card>
  )
}
